import Component from 'inferno-component';
import * as d3 from 'd3';
import './RadialChart.css';
import './RadialChartMobile.css';

const margin = { top: 30, right: 0, bottom: 0, left: 0 };
const dateLabelWidth = 100;
const dateLabelXOffset = 20;

const strDate = (str)=> {
  return new Date(str.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1'))
};

class RadialChart extends Component {
  constructor(props) {
    super(props);
    this.state = { isInitialized: false };
    window.addEventListener('resize', this.resize);
  }

  componentWillReceiveProps(nextProps) {
    if (!this.state.isInitialized) {
      this.init(nextProps.data);
      this.setState({ isInitialized: true, data: nextProps });
    }

    if (nextProps.selectedSkill && this.state.selected !== nextProps.selectedSkill) {
      this.rotateToSelected(nextProps.selectedSkill);
      this.setState({ selected: nextProps.selectedSkill });
    }
  }

  init(data, isUpdate = false) {
    this.initChart(data, isUpdate);
    this.addProjects(data);
    if (!isUpdate) {
      this.initDrag();
    }
  }

  initDrag() {
    let drag = d3.drag()
      .on('start', this.dragstart)
      .on('drag', this.dragged)
      .on('end', this.dragend)

    this.svg
      .on('touchstart', this.dragstart)
      .on('touchmove', this.dragged)
      .on('touchend', this.dragend);

    this.svg.call(drag);
  }

  isMobileLike() {
    return (this.width / 2) < this.height;
  }

  initDimensions() {
    this.width = this.frame.offsetWidth - margin.left - margin.right;
    this.width = this.width < 0 ? 0 : this.width;
    this.height = this.frame.offsetHeight - margin.top - margin.bottom;
    this.height = this.height < 0 ? 0 : this.height;
  }

  getLeftTranslation(radius) {
    if (this.width > 2 * (radius + dateLabelWidth)) {
      // For wide displays place the chart in the center
      return this.width / 2;
    } else if(this.width < (radius + dateLabelWidth)) {
      // For tiny displays place it in the center
      return this.width / 2;
    } else {
      // For small displays place it as close as possible to the date labels
      return radius + dateLabelWidth;
    }
  }

  initTranslation(radius) {
    this.chartTranslation = 'translate(' + this.getLeftTranslation(radius) + ',' + (this.height  + margin.top + margin.bottom) + ')';
  }

  copyDate(date) {
    return new Date(date.getTime())
  }

  findClosestSkill(angle) {
    let numSegments = this.props.data.length;
    let segmentAngle = 360 / numSegments;
    let closestSegment = Math.round(angle / segmentAngle);
    return this.props.data[closestSegment % numSegments];
  }

  getRotationAngle = ()=> {
    // Get and store the rotation angle (from the transform)
    let matches = this.chart.attr('transform').match(/rotate\(([-\d.]+)\)/);
    return parseFloat(matches ? matches[1] : 0);
  }

  dragstart = ()=> {
    // Get and store the initial x
    this.startX = d3.event.x || d3.event.touches[0].clientX;

    // Get and store the rotation angle (from the transform)
    this.startAngle = this.getRotationAngle();
  }

  dragged = ()=> {
    if (d3.event && (d3.event.x || d3.event.touches)) {
      this.speed = ((d3.event.x || d3.event.touches[0].clientX) - this.startX) / 3;
      this.angle = this.startAngle + this.speed;
      this.chart.attr('transform', this.chartTranslation + ' rotate(' + this.angle + ')');
    }
  }

  // Note: This can be simplified
  // Possibly the angle is stored incorrectly
  dragend = ()=> {
    let angle = -this.angle - this.speed;
    if (angle < 0) {
      angle = 360 + (angle % 360);
    }

    let closestSkill = this.findClosestSkill(angle);
    if (closestSkill) {
      this.props.onSelectedSkillChange(closestSkill);

      this.chart
        .transition()
        .duration(1000)
        .ease(d3.easeQuadOut)
        .attr('transform', this.chartTranslation + ' rotate(' + -closestSkill.angle + ')');
    }
  }

  processData(data) {
    this.maxDate = new Date(0);
    this.years = [];

    let minDate = new Date();
    let year = null;

    data.forEach((row)=> {
      row.projects.forEach((project)=> {
        project.date = strDate(project.date);
        year = project.date.getFullYear();
        if (this.years.indexOf(year) < 0) {
          this.years.push(year);
        }
        if (minDate > project.date) {
          minDate = project.date;
        }
        if (this.maxDate < project.date) {
          this.maxDate = project.date;
        }
      });
    });

    this.years.sort();
    this.yearsSmart = this.years.filter(function(year) { return year % 2 });
    this.yearSpan = this.years[this.years.length - 1] - this.years[0];

    // Reduce the min date by 1 year
    this.minDate = this.copyDate(minDate);
    this.minDate.setFullYear( this.minDate.getFullYear() - 1 );
  }

  setSelectedProject = (project, element)=> {
    this.setSelectedElement(element);
    this.props.onSelectProject(project);
  }

  setSelectedElement = (element)=> {
    if (this.state.selectedElement) {
      this.state.selectedElement.classed('selected', false);
    }
    this.setState({ selectedElement: d3.select(element).classed('selected', true) });
  }

  initChart(data, isUpdate = false) {
    this.initDimensions();

    if (isUpdate) {
      this.chart.remove();
    } else {
      this.processData(data);
      this.svg = d3.select(this.svgEl);
    }

    let skills = data.map(function(row) { return row.name });
    let numSkills = skills.length;
    let radius = Math.min(this.width, this.height) - margin.top - margin.bottom;
    radius = radius < 0 ? 0 : radius;
    let labelRadius = radius + 30;
    let leftTranslation = this.getLeftTranslation(labelRadius);
    this.r = d3.scaleTime().domain([this.minDate, this.maxDate]).range([0, radius]);
    this.initTranslation(labelRadius);

    if (this.currentIndicator) {
      this.currentIndicator
        .transition()
        .duration(750)
        .ease(d3.easeBounce)
        .attr('x1', leftTranslation)
        .attr('x2', leftTranslation)
        .attr('y2', this.height + margin.top + margin.bottom);
    } else {
      this.currentIndicator = this.svg.append('line')
        .attr('x1', leftTranslation)
        .attr('y1', 0)
        .attr('x2', leftTranslation)
        .attr('y2', this.height + margin.top + margin.bottom)
        .attr('class', 'current-indicator');
    }

    this.setupDateGroup(isUpdate, dateLabelXOffset, leftTranslation);

    this.chart = this.svg
      .attr('width', this.width + margin.left + margin.right)
      .attr('height', this.height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', this.chartTranslation)
      .attr('class', 'chart');

    // Add circle axis
    let circleGroup = this.chart.append('g')
      .attr('class', 'circles axis')

    circleGroup.selectAll('circle')
      .data(this.r.ticks(this.yearSpan).slice(1))
      .enter()
      .append('circle')
      .attr('r', (d)=> {
        return this.r(d);
      });

    // Add radial "rays" or axis
    let rayGroup = this.chart.append('g')
      .attr('class', 'rays axis');

    rayGroup.selectAll('line')
      .data(data)
      .enter()
      .append('line')
      .attr('y2', -radius - 40)
      .attr('transform', function(d, i) {
        let angle = (i * 360 / numSkills);
        d.angle = angle;
        return 'rotate(' + angle + ')';
      });

    let labels = this.chart.append('g')
      .attr('class', 'labels')

    // Add skill labels
    labels.append('def')
        .append('path')
        .attr('id', 'label-path')
        .attr('d', 'm0 ' + -labelRadius + ' a' + labelRadius + ' ' + labelRadius + ' 0 1,1 -0.01 0')

    labels.selectAll('text')
      .data(skills)
      .enter()
      .append('text')
      .append('textPath')
      .attr('xlink:href', '#label-path')
      .attr('startOffset', function(_, i) {return (i * 100 / numSkills + 2 / numSkills + 0.1) + '%';})
      .text(function(d) {return d; });
  }

  setupDateGroup(isUpdate, dateLabelXOffset, leftTranslation) {
    // Add date axis
    if (!isUpdate) {
      this.dateGroup = this.svg.append('g').attr('class', 'date-groups');

      this.dateGroups = this.dateGroup
        .selectAll('date')
        .data(this.yearsSmart)
        .enter()
        .append('g')
        .attr('class', 'date-group');

      this.dateGroups.append('text')
        .text(function(d) { return d; })
        .attr('x', dateLabelXOffset)
        .attr('y', -5);

      this.dateLines = this.dateGroups.append('line');
    }

    this.dateGroup.attr('transform', 'translate(0,' + (this.height  + margin.top + margin.bottom) + ')');
    this.dateGroups.attr('transform', (d)=> { return 'translate(0,' + -this.r(new Date('' + d)) + ')'; });
    this.dateLines.attr('x2', leftTranslation);
  }

  addProjects(data) {
    // Add projects
    data.forEach((skill, i)=> {
      this.chart.selectAll('project')
        .data(skill.projects)
        .enter()
        .append('circle')
        .attr('cx', (d)=> {
          return this.r(d.date) * Math.sin(skill.angle * Math.PI / 180);
        })
        .attr('cy', (d, i)=> {
          return -this.r(d.date) * Math.cos(skill.angle * Math.PI / 180);
        })
        .attr('r', function(d) { return 3 + (1 * d.complexity); })
        .attr('class', function(d) { return 'project ' + d.type; })
        .on('click', (d)=> {
          this.setSelectedProject(d, d3.event.target);
        })
        .on('mouseover', function(d, i, el) { this.classList.add('active'); })
        .on('mouseout', function(d, i, el) { this.classList.remove('active'); });
    });
  }

  resize = ()=> {
    if (this.state.data) {
      this.init(this.state.data.data, true);
    }
  }

  rotateToSelected(selected) {
    this.chart
      .transition()
      .duration(1200)
      .ease(d3.easeBack)
      .attr('transform', this.chartTranslation + ' rotate(-' + selected.angle + ')');
  }

  render() {
    const legendItemNames = ['selected', 'design', 'personal', 'frontend', 'backend', 'hardware'];
    let legendItems = legendItemNames.map(function(item) {
      return <div className='row'><i className={ item }></i>{ item }</div>
    });
    let legend = <div className="legend">{ legendItems }</div>;

    return (
      <div className="radial-chart" ref={(el) => { this.frame = el; }}>
        <svg ref={(el) => { this.svgEl = el; }}></svg>
        { legend }
        <div classname="legend">
          <div className="row"></div>
        </div>
      </div>
    );
  }
}

export default RadialChart;



// WEBPACK FOOTER //
// ./src/RadialChart/RadialChart.js